import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Game } from '../types';
import GameCard from '../components/GameCard';
import PickForm from '../components/PickForm';
import { fetchGames, createEntry } from '../services/api';
import { format } from 'date-fns';

const Home = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [finalGame, setFinalGame] = useState<Game | null>(null);
  const [contestLocked, setContestLocked] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const [formData, setFormData] = useState({
    xHandle: '',
    xrplAddress: '',
    tieBreakTotalScore: '',
    picks: {} as Record<string, 'home' | 'away'>
  });

  useEffect(() => {
    loadGames();
  }, [selectedDate]);

  const loadGames = async () => {
    try {
      setLoading(true);
      const gamesData = await fetchGames(selectedDate);
      setGames(gamesData.games);
      setFinalGame(gamesData.finalGame || null);
      setContestLocked(gamesData.contestLocked);
      
      // Reset picks for new date
      setFormData(prev => ({ ...prev, picks: {} }));
    } catch (error) {
      toast.error('Failed to load games');
      console.error('Error loading games:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePickChange = (gameId: string, pick: 'home' | 'away') => {
    setFormData(prev => ({
      ...prev,
      picks: { ...prev.picks, [gameId]: pick }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.xHandle.trim()) {
      toast.error('X handle is required');
      return;
    }
    
    if (!formData.xrplAddress.trim()) {
      toast.error('XRPL address is required');
      return;
    }
    
    if (!formData.tieBreakTotalScore || parseInt(formData.tieBreakTotalScore) < 0) {
      toast.error('Valid tie-break total score is required');
      return;
    }

    // Check that all non-started games have picks
    const availableGames = games.filter(game => {
      const startTime = new Date(game.startTime);
      return startTime > new Date() && game.status === 'scheduled';
    });

    const missingPicks = availableGames.filter(game => !formData.picks[game.id]);
    if (missingPicks.length > 0) {
      toast.error(`Please make picks for all available games (${missingPicks.length} missing)`);
      return;
    }

    try {
      setSubmitting(true);
      
      // Convert picks to array format
      const picks = Object.entries(formData.picks).map(([gameId, pick]) => ({
        gameId,
        pick
      }));

      // Create entry in database first
      const entry = await createEntry({
        date: selectedDate,
        xHandle: formData.xHandle.trim(),
        xrplAddress: formData.xrplAddress.trim(),
        tieBreakTotalScore: parseInt(formData.tieBreakTotalScore),
        picks
      });

      toast.success('Entry created! Redirecting to payment...');
      
      // Redirect to status page where payment button is
      window.location.href = `/status/${entry.id}`;
      
    } catch (error: any) {
      toast.error(error.message || 'Failed to create entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRandomPicks = () => {
    const availableGames = games.filter(game => {
      const startTime = new Date(game.startTime);
      return startTime > new Date() && game.status === 'scheduled';
    });

    const randomPicks: Record<string, 'home' | 'away'> = {};
    
    availableGames.forEach(game => {
      // Randomly pick home or away (50/50 chance)
      randomPicks[game.id] = Math.random() < 0.5 ? 'home' : 'away';
    });

    // Also generate a random tie-break total score (realistic NBA range: 180-260)
    const randomTieBreakScore = Math.floor(Math.random() * 81) + 180; // 180-260

    setFormData(prev => ({
      ...prev,
      picks: randomPicks,
      tieBreakTotalScore: randomTieBreakScore.toString()
    }));

    toast.success(`Generated random picks for ${availableGames.length} games!`);
  };

  const availableGamesCount = games.filter(game => {
    const startTime = new Date(game.startTime);
    return startTime > new Date() && game.status === 'scheduled';
  }).length;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">NBA Pick'em Contest</h1>
        <p className="mt-2 text-lg text-gray-600">
          Pick winners for each game • Entry fee: 1000 $NUTS
        </p>
        
        {/* Date Selector */}
        <div className="mt-4 flex justify-center">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="input max-w-xs"
          />
        </div>
      </div>

      {/* Tie-breaker Info */}
      {finalGame && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Tie-breaker Game</h3>
              <p className="text-sm text-blue-700 mt-1">
                {finalGame.awayTeam} @ {finalGame.homeTeam} - Predict the total combined score
              </p>
            </div>
          </div>
        </div>
      )}

      {games.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No games scheduled for {selectedDate}</p>
        </div>
      ) : (
        <>
          {/* Games Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {games.map(game => (
              <GameCard
                key={game.id}
                game={game}
                selectedPick={formData.picks[game.id]}
                onPickChange={(pick) => handlePickChange(game.id, pick)}
                disabled={new Date(game.startTime) <= new Date() || game.status !== 'scheduled'}
                isFinalGame={game.isFinalGame}
              />
            ))}
          </div>

          {/* Contest Status */}
          {contestLocked && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Contest Locked</h3>
                  <p className="text-sm text-red-700 mt-1">
                    The first game of the day has started. No new entries can be submitted for {selectedDate}.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Random Picks Helper */}
          {!contestLocked && availableGamesCount > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-800">Need help with picks?</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Generate random picks for all {availableGamesCount} available games and a tie-break score
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRandomPicks}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  🎲 Random Picks
                </button>
              </div>
            </div>
          )}

          {/* Entry Form */}
          <PickForm
            formData={formData}
            onFormDataChange={setFormData}
            onSubmit={handleSubmit}
            submitting={submitting}
            availableGamesCount={availableGamesCount}
            disabled={contestLocked}
            finalGame={finalGame}
          />
        </>
      )}
    </div>
  );
};

export default Home;
